import { useEffect } from "react";
import { useApolloClient, useQuery } from "@apollo/client";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";

import { EventProvider } from "./EventProvider";
import { updateSchedule } from "./scheduleUpdater";
import { GET_CURRENT_HEATS, GET_EVENT, GET_EVENT_BASE, GET_EVENT_SCHEDULE } from "../../../../graphql/event";
import { GET_EVENT_DIVISION_LEADERBOARD } from "../../../../graphql/eventDivision";
import { GET_HEAT } from "../../../../graphql/heat";
import { useRefocusEffect } from "../../../../hooks/useRefocusEffect";
import { EventPageContainer } from "components/layout/event/EventPageContainer";
import { newManageEventPagesPath } from "components/application";
import { AdminPageContainer } from "components/layout/page/AdminPageContainer";
import { EventPageSkeleton } from "./EventPageSkeleton";
import { OrganisationProvider } from "../../../providers/OrganisationProvider";
import { Event } from "models/Event";
import { useAbortableRequests } from "components/providers/AbortableRequestsProvider";
import { DetachableChannelProvider } from "../../../providers/Realtime/RealtimeProvider";
import { useBindToChannelEvent } from "../../../providers/Realtime/useBindToChannelEvent";
import { useWarning } from "../../../../hooks/useNotifications";

const EventSubscriptions = ({ event, eventChannel }) => {
    const client = useApolloClient();
    const { abortPendingRequest, setAbortableRequest } = useAbortableRequests();

    useBindToChannelEvent({ channelName: eventChannel, skip: !event }, "event-updated", () => client.query({ query: GET_EVENT_BASE, variables: { id: event?.id }, fetchPolicy: "network-only" }));
    useBindToChannelEvent({ channelName: eventChannel, skip: !event }, "schedule-updated", () => client.query({ query: GET_EVENT_SCHEDULE, variables: { id: event?.id }, fetchPolicy: "network-only" }));
    useBindToChannelEvent({ channelName: eventChannel, skip: !event }, "heat-updated", ({ id }) => {
        abortPendingRequest("heat-updated", id);
        setAbortableRequest("heat-updated", id, client.watchQuery({ query: GET_HEAT, variables: { id }, fetchPolicy: "network-only", context: { queryDeduplication: false } }).subscribe());
    });
    useBindToChannelEvent({ channelName: eventChannel, skip: !event }, "current-heats-changed", () => client
        .query({ query: GET_CURRENT_HEATS, variables: { id: event?.id }, fetchPolicy: "network-only" })
        .then(({ data }) => updateSchedule(client, event, data))
    );
    useBindToChannelEvent({ channelName: eventChannel, skip: !event }, "leaderboard-updated", ({ event_division_id, round }) => client.query({ query: GET_EVENT_DIVISION_LEADERBOARD, variables: { id: event_division_id, round }, fetchPolicy: "network-only" }));

    return null;
};

const EventRoot = ({ children }) => {
    const { id } = useParams();
    const { notifyWarning } = useWarning();
    const history = useHistory();
    const { data, refetch } = useQuery(GET_EVENT, { variables: { id } }),
        event = data?.event;
    const isNewManageEventPagesPath = useRouteMatch(newManageEventPagesPath);

    useRefocusEffect(refetch, true);

    useEffect(() => {
        if (data && !data.event) {
            notifyWarning("It looks like we can't find what you are looking for! Perhaps you were looking for one of our organisations?");
            return history.replace("/organisations");
        }
    }, [data]);

    if (data && !data.event) return <EventPageSkeleton />;

    const eventChannel = `event:${id}`;
    const eventObject = event ? Event(event) : event;

    return (
        <EventProvider value={{ event, eventObject, eventChannel }}>
            <OrganisationProvider value={{ organisation: event?.organisation }}>
                <DetachableChannelProvider channelName={eventChannel}>
                    <EventSubscriptions event={event} eventChannel={eventChannel}/>
                    {isNewManageEventPagesPath
                        ? <AdminPageContainer>{children}</AdminPageContainer>
                        : <EventPageContainer>{children}</EventPageContainer>}
                </DetachableChannelProvider>
            </OrganisationProvider>
        </EventProvider>
    );
};
export default EventRoot;
